import React, { useCallback } from 'react'
import { Paper } from '@mui/material'
import { TableComplete } from '@e3dc-react/shell/Elements/Table'
import useModals from 'src/Hooks/useModals'
import { useTableStyles } from '../../shared/styles/table.styles'
import { ChargingSessionModel } from '../models/charging-sessions.model'
import { useTypedSelector } from 'src/State/RootReducer'
import { useDispatch } from 'react-redux'
import { updateChargingSessionsFilters } from 'src/State/E-Mobility/EMobilityStateAction'
import { useChargingSessionTableHeader } from '../hooks/use-e-mobility-charging-session-table-header'

interface ChargingSessionDesktopTableProps {
  chargingSessions: ChargingSessionModel[]
  installationId: number
  shouldDisplayMidMeter: boolean
}

/**
 * Charging sessions table component visible on desktop screen/view >= lg
 *
 * Shows table columns and triggers charging session details popup on row click.
 */
export const ChargingSessionDesktopTable: React.VFC<ChargingSessionDesktopTableProps> = ({
  chargingSessions,
  shouldDisplayMidMeter,
}): React.ReactElement => {
  const { openChargingSessionDetails } = useModals()
  const { classes } = useTableStyles()

  const chargingSessionFilters = useTypedSelector(state => state.eMobility.chargingSessionsFilters.filters)
  const dispatch = useDispatch()

  const header = useChargingSessionTableHeader({ shouldDisplayMidMeter })

  const handleRowClick = useCallback(
    (row: ChargingSessionModel) => {
      const session = chargingSessions.find(session => session.sessionId === row.sessionId)

      if (!session) {
        return
      }
      openChargingSessionDetails({
        session,
        shouldDisplayMidMeter,
      })
    },
    [chargingSessions, openChargingSessionDetails, shouldDisplayMidMeter]
  )

  return (
    <Paper className={classes.tableContainer} id="e-mobility-charging-sessions-table">
      <TableComplete
        header={header}
        data={chargingSessions}
        onSortChange={sort => dispatch(updateChargingSessionsFilters({ ...chargingSessionFilters, sort, page: 0 }))}
        setResultLimit={value => dispatch(updateChargingSessionsFilters({ ...chargingSessionFilters, limit: value, page: 0 }))}
        resultLimit={chargingSessionFilters?.limit ?? 10}
        resultPage={chargingSessionFilters?.page ?? 0}
        setResultPage={value => dispatch(updateChargingSessionsFilters({ ...chargingSessionFilters, page: value }))}
        initialSort={chargingSessionFilters?.sort && { name: chargingSessionFilters.sort.name, direction: chargingSessionFilters.sort.direction }}
        coloredColumn
        onSelect={handleRowClick}
      />
    </Paper>
  )
}
